import type {
  IAgoraRTCClient,
  IMicrophoneAudioTrack,
  ICameraVideoTrack,
  IAgoraRTCRemoteUser
} from 'agora-rtc-sdk-ng'
import type { roomPkInterface } from '~/types/apiType/roomPkType'
let client = undefined as IAgoraRTCClient | undefined
// Vue3 Proxy代理会导致和对象指针不一致，因AgoraSDK的 Api 会检验传的user对象和client里存的user对象是否指针一致，所以不存pinia。但会和remoteUsers一直同步数据
let agoraRemoteUsers = {} as { [prop: string]: IAgoraRTCRemoteUser } // 远程用户、主播
let count = 0
export const useAgoraStore = defineStore({
  id: 'agora',
  state: () => ({
    agoraAppid: '' as string, // appId
    channelToken: '' as string, // 进入频道token
    channelTokenMin: '' as string, // 上麦token
    role: '' as 'host' | 'audience' | '', // 直播角色 host:主播，audience:观众
    mode: 'live' as 'live' | 'rtc', // 场景值 live：直播场景，观众不可以发送轨道；rtc: 通信场景,如需要发送轨道
    isRequestedAudioTrack: false as boolean, // 是否正在请求音频轨道
    audioTrack: undefined as IMicrophoneAudioTrack | undefined, // 本地音频轨道
    videoTrack: undefined as ICameraVideoTrack | undefined, // 本地视频轨道
    remoteUsers: {} as { [prop: string]: IAgoraRTCRemoteUser }, // 远程用户、主播
    joined: false as boolean, // 是否跑完整个流程进房或发布
    authPopupConfig: {
      // 授权弹窗
      isShow: false, // 是否显示
      isLazyShow: false // 是否加载
    },
    isSkipAuth: false, // agora.getDevices 是否跳过权限
    isAutoplayFailed: false, //是否自动播放失败，用户从未点击过页面有可能会播放音频失败
    initedMicrophone: false as boolean // 是否已经初始化麦克风
  }),
  actions: {
    //#region 获取腾讯签名参数相关
    // 设置腾讯签名参数
    setAgoraSDKParams(appId: string, channelToken: string) {
      this.agoraAppid = appId
      this.channelToken = channelToken
    },
    // 设置腾讯上麦签名参数
    setAgoraSDKMinParams(channelTokenMin: string) {
      this.channelTokenMin = channelTokenMin
    },
    // 设置socket返回的channelToken
    setChannelToken(key: string) {
      this.channelToken = key
    },
    //#endregion

    // 检测是否支持浏览器
    checkBrowser() {
      const i18n = useNuxtApp().$i18n
      return new Promise((resolve, reject) => {
        // 测试浏览器是否支持 result 为 true 代表支持，false 代表不支持；facebook内置浏览器打开忽略此判断
        const AgoraRTC = window.AgoraRTC
        if (
          AgoraRTC.checkSystemRequirements &&
          !AgoraRTC.checkSystemRequirements() &&
          !common.checkVersion().facebook
        ) {
          closeToast()
          showConfirmDialog({
            message: i18n.t('call.googleChromeTips'),
            confirmButtonText: i18n.t('public.Confirm'),
            showCancelButton: false,
            className: 'private-dialog-customize single-btn'
          }).catch((error) => {
            console.log(error)
          })
          reject(false)
        } else {
          resolve(true)
        }
      })
    },
    // 检测权限设备,检查是否有音视频设备权限
    checkTracks() {
      return new Promise((resolve, reject) => {
        this.checkBrowser()
          .then(() => {
            window.AgoraRTC.getDevices(this.isSkipAuth)
              .then((devices) => {
                if (devices && devices.length) {
                  let audioinput = false
                  // let videoinput = false
                  const isNormal = devices.some((v) => {
                    if (v.kind === 'audioinput') {
                      audioinput = true
                    }
                    // if (v.kind === 'videoinput') {
                    //   videoinput = true
                    // }
                    // return audioinput && videoinput
                    console.log(devices)
                    console.log(audioinput)
                    return audioinput
                  })
                  if (isNormal) {
                    this.isSkipAuth = true // 成功一次就跳过权限
                    resolve('success')
                  } else {
                    this.setAuthPopupConfig(true)
                    reject('DEVICE_NOT_FOUND')
                  }
                } else {
                  this.setAuthPopupConfig(true)
                  reject('DEVICE_NOT_FOUND')
                }
              })
              .catch((error) => {
                this.setAuthPopupConfig(true)
                reject(JSON.stringify(error))
              })
          })
          .catch((error) => reject(error))
      })
    },
    // 创建实例
    async create() {
      console.log('AgoraSDK => 创建实例')
      const roomStore = useRoomStore()
      const audioStore = useAudioStore()
      const i18n = useNuxtApp().$i18n

      // 视频的房间才显示遮罩层
      this.setIsShowMask(true)

      // 因观看直播操作比较快，所以先判断一下是否需要离开函数
      if (client?.channelName) {
        await client?.leave()
      }
      // 根据房间类型获取场景值
      const mode =
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ? 'rtc' : 'live'

      // 判断是否需要重新创建实例或新建实例
      if (!client) {
        console.log('AgoraSDK => 重新创建实例')
        const AgoraRTC = window.AgoraRTC
        // 监听自动播放失败
        AgoraRTC.onAutoplayFailed = () => {
          // 引导用户点击页面，可以弹窗提示。不在IOSWebView才弹
          // if (!common.checkVersion().app) {
          showConfirmDialog({
            message: i18n.t('room.AtuoPlayFailedMsg'),
            confirmButtonText: i18n.t('public.Confirm'),
            showCancelButton: false,
            className: 'private-dialog-customize single-btn'
          }).catch((error) => {
            console.log(error)
          })
          // }
        }
        AgoraRTC.enableLogUpload() // 日志上传
        AgoraRTC.setLogLevel(4) // 日志输出级别 0:DEBUG 1:INFO 2:WARNING 3:ERROR 4:NONE
        client = AgoraRTC.createClient({
          mode,
          codec: 'vp8'
        })
      }
    },
    // 进入频道
    async join() {
      const roomStore = useRoomStore()
      const audioStore = useAudioStore()

      // 视频房、通话房才开启定时器
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        roomStore.setIsTimeout() //开启定时器
      }

      if (!this.channelToken) return
      console.log('AgoraSDK => 开始join')
      try {
        // 用户在麦上就切换角色
        await this.setRole(audioStore.isOnMicSeat ? 'host' : 'audience')
        // 将事件侦听器添加到client
        client?.on('user-published', this.onUserPublished) // 当远端用户成功发布音视频轨道之后
        client?.on('user-unpublished', this.onUserUnpublished) // 远端用户主动取消发布
        client?.enableAudioVolumeIndicator() // 开启音量回调
        client?.on('volume-indicator', (result) => {
          // 传参设置音量
          audioStore.setVolumeIndicator(
            result.map((v) => {
              return {
                uid: Number(v.uid),
                volume: v.level
              }
            })
          )
        }) // 监听音量范围操作，可以用来做麦位光圈操作

        // 进入频道  2464758e714e4ff1a92edfe6fd4b2aa3
        const userStore = useUserStore()
        console.log(
          this.role === 'host' ? this.channelTokenMin : this.channelToken
        )
        const isJoin = await client?.join(
          this.agoraAppid,
          roomStore.roomId.toString(),
          this.role === 'host' ? this.channelTokenMin : this.channelToken,
          userStore.userInfo.uid
        )
        console.log(
          'AgoraSDK => 加入频道',
          isJoin ? '成功' : '失败',
          roomStore.roomId.toString(),
          userStore.userInfo.uid
        )

        // 通话房的采集设备
        if (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
          await this.initAllTracks()
          const tracks: [IMicrophoneAudioTrack, ICameraVideoTrack] = [
            this.audioTrack as IMicrophoneAudioTrack,
            this.videoTrack as ICameraVideoTrack
          ]
          await client?.publish(tracks).then(() => {
            console.log('AgoraSDK => 发布成功')
            // 发布成功，判断清除发布超时
            roomStore.closeIsTimeout({ isPublishTimeout: true })
          })
        }

        // 语音房初始化时 如果用户自己在麦位上就发布本地音频轨道
        if (
          roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
          audioStore.isOnMicSeat
        ) {
          const micInfo = audioStore.queueList.find(
            (item) => item.value.member?.uid === userStore.userInfo.uid
          )?.value.mic_info
          this.manageLocalAudioMicrophone(!micInfo?.micState)
        }

        this.joined = true
        // 如果还没此状态还没更新，就更新
        if (roomStore.isShowMask) {
          this.setIsShowMask(roomStore.isHostLive)
        }
      } catch (error) {
        console.log('AgoraSDK => 进房函数报错', error)
      }
    },
    // 开始跨频道媒体流转发
    async startChannelMediaRelay(data: roomPkInterface) {
      // const roomStore = useRoomStore()
      // const AgoraRTC = window.AgoraRTC
      // channelMediaConfig = AgoraRTC.createChannelMediaRelayConfiguration()
      // // 设置源频道信息
      // channelMediaConfig.setSrcChannelInfo({
      //   // 源频道名
      //   channelName: roomStore.roomId.toString(),
      //   // 源频道里需要转发的主播的 uid
      //   uid: roomStore.roomUid,
      //   // 需要用为 0 的 uid 和源频道名重新生成
      //   token: data.agoraKey
      // })
      // // 设置目标频道信息，可多次调用，最多设置 4 个目标频道
      // channelMediaConfig.addDestChannelInfo({
      //   // 目标频道名
      //   channelName: data.pkRoomId.toString(),
      //   // 用于标识目标频道中被转发的媒体流
      //   // uid 可为 0，也可自行设置，需确保与目标频道中的其他用户 ID 不同，防止造成互踢
      //   uid: data.pkRoomUid,
      //   // 需要用 uid 和目标频道名生成
      //   token: data.otherAgoraKey
      // })
      // client?.on('channel-media-relay-state', (state) => {
      //   console.log('channel-media-relay-state', state)
      // })
      // client
      //   ?.startChannelMediaRelay(channelMediaConfig)
      //   .then(() => {
      //     console.log(`startChannelMediaRelay success`)
      //   })
      //   .catch((e) => {
      //     console.log(`startChannelMediaRelay failed`, e)
      //   })
    },
    // 停止跨频道媒体流转发
    async stopChannelMediaRelay() {
      // const roomPkStore = useRoomPkStore()
      // // 删除一个目标频道
      // channelMediaConfig?.removeDestChannelInfo(
      //   roomPkStore.roomPkInfo.pkRoomId?.toString()
      // )
      // 停止跨频道媒体流转发
      // client
      //   ?.stopChannelMediaRelay()
      //   .then(() => {
      //     console.log('stop media relay success')
      //   })
      //   .catch((e) => {
      //     console.log('stop media relay failed', e)
      //   })
    },
    /**
     * 订阅/取消订阅远端音频
     * @param uid 目标uid
     * @param isSubscribe 是否订阅音频
     */
    async switchRemoteAudio(uid: number, isSubscribe: boolean) {
      const remoteUser = agoraRemoteUsers[uid]
      if (remoteUser) {
        if (isSubscribe) {
          await client?.subscribe(remoteUser, 'audio')
        } else {
          await client?.unsubscribe(remoteUser, 'audio')
        }
      }
    },
    /**
     * 设置角色
     */
    async setRole(value: 'host' | 'audience') {
      // 不同值才设置
      if (this.role !== value) {
        console.log('AgoraSDK => 开始设置角色', value)
        this.role = value
        // 如果是主播
        if (this.role == 'host') {
          // 如果没有上麦key就获取
          if (!this.channelTokenMin) {
            const roomStore = useRoomStore()
            await roomStore.getSDKMinToken()
          }
          // 如果进房成功
          if (this.joined) {
            await client?.renewToken(this.channelTokenMin)
          }
          await client?.setClientRole(this.role)

          console.log('AgoraSDK => 设置主播角色成功', value)
        } else if (this.role === 'audience') {
          // 如果是其他角色
          // 如果进房成功
          if (this.joined) {
            await client?.renewToken(this.channelToken)
          }
          await client?.setClientRole(this.role, { level: 1 })
          console.log('AgoraSDK => 设置观众角色成功', value)
        }
      }
    },
    // 采集设备,适用通话房
    async initAllTracks() {
      console.log('AgoraSDK => 采集设备，1v1房间')
      this.audioTrack && this.audioTrack.close()
      this.videoTrack && this.videoTrack.close()
      let tracks: [IMicrophoneAudioTrack, ICameraVideoTrack]
      const AgoraRTC = window.AgoraRTC
      try {
        tracks = await Promise.all([
          AgoraRTC.createMicrophoneAudioTrack(),
          AgoraRTC.createCameraVideoTrack({
            encoderConfig: {
              width: 480,
              height: 640,
              frameRate: 15,
              bitrateMin: 1200,
              bitrateMax: 1200
            }
          })
        ])
        this.audioTrack = tracks[0]
        this.videoTrack = tracks[1]
      } catch (error) {
        console.log('AgoraSDK => 通话房采集视频失败', error)
        this.audioTrack && this.audioTrack.close()
        this.videoTrack && this.videoTrack.close()
        tracks = await Promise.all([
          AgoraRTC.createMicrophoneAudioTrack(),
          AgoraRTC.createCameraVideoTrack({
            encoderConfig: {
              width: 480,
              height: 640,
              frameRate: 15,
              bitrateMin: 1200,
              bitrateMax: 1200
            }
          })
        ])
        this.audioTrack = tracks[0]
        this.videoTrack = tracks[1]
        console.log(this.audioTrack, this.videoTrack)
      }
    },
    // 当远端用户成功发布音视频轨道之后
    async onUserPublished(user, mediaType) {
      console.log('AgoraSDK => 远程用户', mediaType, user)
      const roomStore = useRoomStore()
      const audioStore = useAudioStore()
      const roomPkStore = useRoomPkStore()
      // 视频房或通话房目前只接收主播的流或者PK时对面主播的流，如果不是主播的流就return
      if (
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
          roomStore.roomUid != user.uid) ||
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
          roomStore.roomUid != user.uid &&
          roomPkStore.roomPkInfo.pkRoomUid != user.uid)
      ) {
        console.log('AgoraSDK => 远程用户,不是主播的流', mediaType, user)
        return
      }

      // 视频才显示遮罩层
      if (mediaType === 'video') {
        this.setIsShowMask(true)
      }

      // 发起订阅，视频房和通话房默认订阅 语音房扬声器打开了也默认订阅
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
        audioStore.isOpenSound
      ) {
        await client?.subscribe(user, mediaType)
      }
      delete this.remoteUsers[user.uid]
      delete agoraRemoteUsers[user.uid]
      this.remoteUsers[user.uid] = user // 设置远程用户
      agoraRemoteUsers[user.uid] = user // 设置远程用户

      // 视频轨道或语音房才操作远程用户, 监听远程用户
      if (
        mediaType === 'video' ||
        (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
          mediaType === 'audio')
      ) {
        this.remoteUsersChange(false)
      }

      // 播放语音 如果需要自动播放就用
      if (
        roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
        mediaType === 'audio' &&
        audioStore.isOpenSound
      ) {
        user.audioTrack.play()
      }

      // 监听首帧视频解码时触发，视频轨道才会触发
      this.remoteUsers[user.uid].videoTrack?.on(
        'first-frame-decoded',
        async () => {
          console.log('AgoraSDK => 远程用户首帧视频解码时触发', user, mediaType)
          this.setIsShowMask(false)
          // 发布成功，判断清除发布超时
          roomStore.closeIsTimeout({ isPirstFrameTimeout: true })

          // 兼容安卓，让安卓移除遮挡层
          if (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
            const socketStore = useSocketStore()
            const callStore = useCallStore()
            const req_data = {
              custom: {
                first: 40,
                second: 24
              },
              uid: socketStore.memberUserInfo.uid,
              member: JSON.stringify(socketStore.memberUserInfo),
              room_id: roomStore.roomInfo.roomId,
              timestamp: new Date().getTime()
            }
            socketStore.sendMessage(req_data)
            callStore.onStartBillingNew()
          }
        }
      )
    },
    // 用户端主动取消发布
    onUserUnpublished(user, mediaType) {
      console.log('AgoraSDK => 离开的远程用户', mediaType, user)
      const roomStore = useRoomStore()

      // 视频直播或视频通话房 移除离开的远程用户
      if (
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
          mediaType === 'video') ||
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
          mediaType === 'video')
      ) {
        delete this.remoteUsers[user.uid]
        delete agoraRemoteUsers[user.uid]
        this.remoteUsersChange(false) // 监听远程用户
      }
      // 语音房
      if (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
        delete this.remoteUsers[user.uid]
        delete agoraRemoteUsers[user.uid]
        this.remoteUsersChange(false) // 监听远程用户
      }
      // 取消订阅
      // await client.unsubscribe(user, mediaType)
    },

    /**
     * 语音房使用
     * 管理本地上下麦克风
     * @param {boolean} isPut true:上麦, false:下麦
     */
    async manageLocalAudioMicrophone(isPut: boolean) {
      console.log('AgoraSDK => 语音房管理麦克风', isPut)
      const AgoraRTC = window.AgoraRTC
      // 操作麦克风就切换主播角色，上麦先切换身份再推流，下麦先断流再切身份
      // 有本地音频轨道就发布
      if (this.audioTrack) {
        if (isPut) {
          await this.setRole('host')
          await this.audioTrack.setEnabled(true)
          client?.publish(this.audioTrack as IMicrophoneAudioTrack) // 发布
        } else {
          await client?.unpublish(this.audioTrack as IMicrophoneAudioTrack) // 取消发布
          await this.audioTrack.setEnabled(false)
          this.setRole('audience')
        }
      } else {
        // 如果没有本地音频
        if (this.isRequestedAudioTrack) return
        this.isRequestedAudioTrack = true
        console.log('开始创建麦克风 -----------------> ', ++count)

        AgoraRTC.createMicrophoneAudioTrack()
          .then(async (audioTrack: IMicrophoneAudioTrack) => {
            console.log('创建麦克风成功')

            this.isRequestedAudioTrack = false
            this.audioTrack && this.audioTrack.close()
            this.audioTrack = audioTrack
            this.initedMicrophone = true
            if (isPut) {
              await this.setRole('host')
              await this.audioTrack.setEnabled(true)
              client?.publish(this.audioTrack as IMicrophoneAudioTrack) // 发布
              // this.audioTrack.play()
            } else {
              await client?.unpublish(this.audioTrack as IMicrophoneAudioTrack) // 取消发布
              await this.audioTrack.setEnabled(false)
              this.setRole('audience')
            }
          })
          .catch((err) => {
            console.log('创建麦克风失败', JSON.stringify(err))
            if (JSON.stringify(err).indexOf('PERMISSION_DENIED') != -1) {
              const audioStore = useAudioStore()
              // 麦克风图标显示
              audioStore.setIsOpenMic(false)
              // 授权“麦克风”权限
              this.setAuthPopupConfig(true)
            }
          })
      }
    },
    /**
     * 语音房使用
     * 开关远程所有音频轨道
     * @param {boolean} isPlay true:播放, false:不播放
     */
    switchAllRemoteAudioTrack(isPlay: boolean) {
      console.log('AgoraSDK => 语音房是否播放音频', isPlay)
      if (isPlay) {
        // 播放全部远端音频
        Object.keys(agoraRemoteUsers).forEach(async (v) => {
          if (agoraRemoteUsers[v]) {
            await client?.subscribe(agoraRemoteUsers[v], 'audio')
            agoraRemoteUsers[v].audioTrack?.play()
          }
        })
      } else {
        // 取消全部远端音频
        Object.keys(agoraRemoteUsers).forEach(async (v) => {
          if (agoraRemoteUsers[v]) {
            await client?.unsubscribe(agoraRemoteUsers[v], 'audio')
          }
        })
      }
    },
    // room遮罩层设置,视频的房间才可以操作遮罩层
    setIsShowMask(show: boolean) {
      const roomStore = useRoomStore()
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        roomStore.setIsShowMask(show)
      }
    },
    /**
     * 监听远程用户
     * @param {boolean} isInit 是否初始化
     */
    remoteUsersChange(init: boolean) {
      const roomStore = useRoomStore()
      roomStore.whetherHostIsLive(init ? [] : Object.keys(this.remoteUsers)) // 监听远程用户
    },
    /**
     * 显示授权弹窗
     */
    setAuthPopupConfig(value: boolean) {
      this.authPopupConfig.isLazyShow = value
      this.authPopupConfig.isShow = value
    },
    // 离开
    async leave() {
      const roomStore = useRoomStore()
      const publicStore = usePublicStore()
      // 强制退出才清除
      if (
        roomStore.quitRoomType === 0 ||
        roomStore.quitRoomType === 1 ||
        roomStore.quitRoomType === 3
      ) {
        // 监听远程用户
        this.remoteUsersChange(true)
        await client?.leave()
        this.resetData()
        window.AgoraRTC.enableLogUpload()
        console.log('AgoraSDK => 离开成功')
        if (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE) {
          // 离开成功，提交事件
          emitter.emit(EmitterEventType.RTC_LEAVE_SUCCESS)
        }
      }
    },
    // 重置数据
    resetData() {
      const audioStore = useAudioStore()
      audioStore.setVolumeIndicator([])
      this.audioTrack && this.audioTrack.close()
      this.audioTrack = undefined
      this.videoTrack && this.videoTrack.close()
      this.videoTrack = undefined
      this.remoteUsers = {}
      agoraRemoteUsers = {}
      this.joined = false
      this.channelToken = ''
      this.channelTokenMin = ''
      this.agoraAppid = ''
      this.role = ''
    }
  }
})
